import axiosWithAuth from '../utils/axiosWithAuth';

/**
 * Calculate repair breakdown based on property, style, and tariff
 * @param {Object} params - Calculation parameters
 * @param {string} params.styleId - Style ID
 * @param {string} params.tariffId - Tariff ID
 * @param {Array} params.roomsData - Array of room data objects
 * @param {number} params.doorCount - Number of doors
 * @param {number} params.windowCount - Number of windows
 * @returns {Promise<Object>} - The calculation result
 */
export const calculateRepairBreakdown = async (params) => {
  try {
    // Validate required parameters
    if (!params.styleId || !params.tariffId) {
      throw new Error('Style ID and Tariff ID are required');
    }
    
    // Ensure proper data structure
    const calculationData = {
      repairStyle: { id: params.styleId },
      repairTariff: { id: params.tariffId },
      property: {
        totalArea: parseFloat(params.property?.totalArea || 0),
        roomHeight: parseFloat(params.property?.roomHeight || 2.7),
        roomValues: params.roomsData || [],
        totalDoorCount: parseInt(params.doorCount || 0),
        totalWindowCount: parseInt(params.windowCount || 0)
      },
      materials: { 
        roomTypes: Array.isArray(params.roomsData) ? params.roomsData.map(room => ({
          roomType: room.roomType,
          materials: Array.isArray(room.materials) ? room.materials.map(material => ({
            id: material.id,
            materialId: material.materialId,
            type: material.type || 'standard',
            name: material.name,
            price: parseFloat(material.price || 0),
            servicePrice: parseFloat(material.servicePrice || 0),
            count: parseInt(material.count || 1),
            area: parseFloat(material.area || 0),
            totalArea: parseFloat(material.totalArea || 0),
            currency: material.currency || 'AZN',
            isDependentMaterial: Boolean(material.isDependentMaterial),
            dependentMaterials: Array.isArray(material.dependentMaterials) ? material.dependentMaterials : []
          })) : []
        })) : []
      }
    };
    
    console.log('Calculating repair breakdown with structured data:', calculationData);
    
    const api = axiosWithAuth();
    const response = await api.post('/repairs/calculate-breakdown', calculationData);
    
    // Validate response structure
    if (!response.data || !response.data.breakdown || !response.data.costs) {
      throw new Error('Invalid response structure from calculation endpoint');
    }
    
    return response.data;
  } catch (error) {
    console.error('Error calculating repair breakdown:', error);
    throw error;
  }
};

/**
 * Save calculation result to database
 * @param {Object} data - Calculation data to save
 * @returns {Promise<Object>} - Saved calculation result
 */
export const saveCalculation = async (data) => {
  try {
    // Ensure proper data structure for saving
    const saveData = {
      tempId: data.tempId,
      breakdown: {
        rooms: Array.isArray(data.breakdown?.rooms) ? data.breakdown.rooms : [],
        fixtures: {
          doors: {
            count: parseInt(data.breakdown?.fixtures?.doors?.count || 0),
            materialCost: parseFloat(data.breakdown?.fixtures?.doors?.materialCost || 0),
            serviceCost: parseFloat(data.breakdown?.fixtures?.doors?.serviceCost || 0)
          },
          windows: {
            count: parseInt(data.breakdown?.fixtures?.windows?.count || 0),
            materialCost: parseFloat(data.breakdown?.fixtures?.windows?.materialCost || 0),
            serviceCost: parseFloat(data.breakdown?.fixtures?.windows?.serviceCost || 0)
          }
        }
      },
      costs: {
        materialsCost: parseFloat(data.costs?.materialsCost || 0),
        serviceCost: parseFloat(data.costs?.serviceCost || 0),
        generalCost: parseFloat(data.costs?.generalCost || 0),
        doorMaterialsCost: parseFloat(data.costs?.doorMaterialsCost || 0),
        windowMaterialsCost: parseFloat(data.costs?.windowMaterialsCost || 0),
        roomMaterialsCost: parseFloat(data.costs?.roomMaterialsCost || 0),
        doorServiceCost: parseFloat(data.costs?.doorServiceCost || 0),
        windowServiceCost: parseFloat(data.costs?.windowServiceCost || 0),
        roomServiceCost: parseFloat(data.costs?.roomServiceCost || 0),
        dependentMaterialsCost: parseFloat(data.costs?.dependentMaterialsCost || 0),
        dependentServiceCost: parseFloat(data.costs?.dependentServiceCost || 0),
        currency: data.costs?.currency || 'AZN',
        duration: parseInt(data.costs?.duration || 1)
      },
      selectedMaterial: data.selectedMaterial || {}
    };

    const api = axiosWithAuth();
    const response = await api.post('/repairs/calculations', saveData);
    return response.data;
  } catch (error) {
    console.error('Error saving calculation:', error);
    throw error;
  }
};

/**
 * Get a saved breakdown by ID
 * @param {string} breakdownId - The breakdown ID
 * @returns {Promise<Object>} - The breakdown data
 */
export const getBreakdownById = async (breakdownId) => {
  try {
    const api = axiosWithAuth();
    const response = await api.get(`/repairs/breakdown/${breakdownId}`);
    
    return response.data;
  } catch (error) {
    console.error('Error fetching breakdown:', error);
    throw error;
  }
};

const calculationService = {
  calculateRepairBreakdown,
  getBreakdownById,
  saveCalculation
};

export default calculationService; 